/**
 * URL 검색 파라미터 파서 — TanStack Router validateSearch 공용.
 * 잘못된 값은 버린다(undefined) — 공유 링크가 깨져도 화면은 기본값으로 열린다.
 */

export const parseString = (value: unknown): string | undefined => {
  if (typeof value === 'number') return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/** 1 이상의 정수만 */
export const parsePositiveInt = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : Number(parseString(value));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const parseEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | undefined => (allowed.includes(value as T) ? (value as T) : undefined);

/** true일 때만 남긴다 (false는 기본값이라 URL에서 뺀다) */
export const parseTrue = (value: unknown): true | undefined =>
  value === true || value === 'true' ? true : undefined;

/** yyyy */
export const parseYear = (value: unknown): string | undefined => {
  const parsed = parseString(value);
  return parsed && /^\d{4}$/.test(parsed) ? parsed : undefined;
};

/** yyyyMM */
export const parseYearMonth = (value: unknown): string | undefined => {
  const parsed = parseString(value);
  return parsed && /^\d{4}(0[1-9]|1[0-2])$/.test(parsed) ? parsed : undefined;
};

/** 값 하나 또는 배열 → 중복 없는 배열. 비면 undefined */
const parseList = <T>(
  value: unknown,
  parseItem: (item: unknown) => T | undefined,
): T[] | undefined => {
  const items = (Array.isArray(value) ? value : [value])
    .map(parseItem)
    .filter((item): item is T => item !== undefined);
  const unique = [...new Set(items)];
  return unique.length > 0 ? unique : undefined;
};

export const parsePositiveIntList = (value: unknown) =>
  parseList(value, parsePositiveInt);

/** '보기' 개수 — 선택지에 없는 값은 버린다 (기본값은 URL에 남기지 않는다) */
export const parsePageSize = (
  value: unknown,
  allowed: readonly number[],
): number | undefined => {
  const size = parsePositiveInt(value);
  return size !== undefined && allowed.includes(size) ? size : undefined;
};
